import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { apiConfig } from '@/config/apiConfig';

// Extend AxiosRequestConfig to include custom properties
declare module 'axios' {
  interface AxiosRequestConfig {
    metadata?: {
      startTime: Date;
    };
    _retry?: boolean;
    _skipRetry?: boolean;
  }
}

class ApiClient {
  private client: AxiosInstance;
  private retryCount = new Map<string, number>();

  constructor() {
    this.client = axios.create({
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout,
      headers: apiConfig.headers,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor for authentication
    this.client.interceptors.request.use(
      (config) => {
        // Check both localStorage and sessionStorage for token
        const token = localStorage.getItem(apiConfig.auth.tokenKey) || 
                      sessionStorage.getItem(apiConfig.auth.tokenKey);
        
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add request timestamp for debugging
        config.metadata = { startTime: new Date() };
        return config;
      },
      (error) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response) => {
        // Clear retry count on successful response
        this.retryCount.delete(response.config.url || '');
        
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;
        
        if (!originalRequest) {
          return Promise.reject(error);
        }

        // Handle authentication errors
        if (error.response?.status === 401) {
          await this.handleAuthError(originalRequest);
          return this.client(originalRequest);
        }

        // Handle network errors with retry (skip for opted-out requests like exports)
        if (
          !originalRequest._skipRetry &&
          (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED')
        ) {
          const retryAttempt = await this.handleRetry(originalRequest, error);
          if (retryAttempt) {
            return retryAttempt;
          }
        }

        // Log error for debugging
        console.error('[API Error]', {
          url: originalRequest.url,
          method: originalRequest.method,
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });

        return Promise.reject(error);
      }
    );
  }

  private async handleAuthError(originalRequest: AxiosRequestConfig): Promise<void> {
    // Check both storages for refresh token
    const refreshToken = localStorage.getItem(apiConfig.auth.refreshTokenKey) || 
                         sessionStorage.getItem(apiConfig.auth.refreshTokenKey);
    
    if (refreshToken && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const response = await axios.post(
          `${apiConfig.baseURL}${apiConfig.endpoints.auth.refresh}`,
          { refresh_token: refreshToken }
        );
        
        const { token } = response.data;
        
        // Determine which storage to use (prefer localStorage if it has the refresh token)
        const storage = localStorage.getItem(apiConfig.auth.refreshTokenKey) ? localStorage : sessionStorage;
        storage.setItem(apiConfig.auth.tokenKey, token);
        
        // Update authorization header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
      } catch (refreshError: any) {
        // Only clear tokens and redirect on actual auth failures
        if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
          console.log('ApiClient - Refresh token invalid, clearing tokens and redirecting to login');
          this.clearAuthTokens();
          window.location.href = '/login';
        } else {
          // Network or server error - don't clear tokens, just fail the request
          console.warn('ApiClient - Refresh token request failed (network/server error):', refreshError.message);
        }
        throw refreshError;
      }
    } else {
      // No refresh token or already retried, redirect to login
      this.clearAuthTokens();
      window.location.href = '/login';
      throw new Error('Authentication failed');
    }
  }

  private clearAuthTokens() {
    // Clear tokens from both localStorage and sessionStorage
    localStorage.removeItem(apiConfig.auth.tokenKey);
    localStorage.removeItem(apiConfig.auth.refreshTokenKey);
    sessionStorage.removeItem(apiConfig.auth.tokenKey);
    sessionStorage.removeItem(apiConfig.auth.refreshTokenKey);
  }

  private async handleRetry(
    originalRequest: AxiosRequestConfig, 
    error: AxiosError
  ): Promise<any> {
    const url = originalRequest.url || '';
    const currentRetries = this.retryCount.get(url) || 0;
    
    if (currentRetries < apiConfig.retry.attempts) {
      this.retryCount.set(url, currentRetries + 1);
      
      // Wait before retrying
      await new Promise(resolve => 
        setTimeout(resolve, apiConfig.retry.delay * (currentRetries + 1))
      );
      
      return this.client(originalRequest);
    }
    
    // Max retries reached
    this.retryCount.delete(url);
    return Promise.reject(error);
  }

  // Public API methods
  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config);
  }

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.post<T>(url, data, config);
  }

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.patch<T>(url, data, config);
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.put<T>(url, data, config);
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T>(url, config);
  }

  // Helper method to check API health
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.client.get('/health');
      return response.status === 200;
    } catch {
      return false;
    }
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();
export default apiClient;